import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, catchError, of } from "rxjs";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { environment } from "../../../../environments/environment";

import {
  donorBasicMetricKeys,
  donorPhysicalAndMentalKeys,
  donorHealthHistoryKeys,
  donorFamilyHistoryKeys,
  donorSocialHistoryKeys,
  donorDietHistoryKeys,
  basicMetricsLabels,
  physicalAndMentalHistoryLabels,
  healthHistoryLabels,
  familyHistoryLabels,
  socialHistoryLabels,
  dietHistoryLabels,
  statusMapping,
  synopsisMetricRules,
} from "../../../models/constants/synopsis-mappings";
import { DonorMetric } from "../../../models/donor-metric.model";
import {
  ProfileInfo,
} from "../../../models/donorProfile.model";
import { JourneyQuestion } from "../../../models/journey-question.model";
import {
  Question,
  QuestionnaireTemplate,
  Subsection,
  VisibilityLogic,
} from "../../../models/questionnaire.model";
import { AuthService } from "../../../services/auth.service";
import { ContentService } from "../../../services/content.service";
import { NavigationService } from "../../../services/navigation.service";
import { QuestionnaireService } from "../../../services/questionnaire.service";
import { ResponseService } from "../../../services/response.service";
import { ScaleConfig } from "../../../shared/sliding-scale/sliding-scale.component";
import { calculateAge } from "../../../utils/utils";
import { EpicLookupModalComponent } from "../../../shared/epic-lookup-modal/epic-lookup-modal.component";
import { Grouped, MetricEntry } from "../../../models/synopsis.models";

type MetricStatus =
  | "risk"
  | "cleared"
  | "notFilled"
  | "notRequired"
  | "display";

type TranslationState = {
  translatedText?: string;
  loading?: boolean;
  error?: string;
};

@Component({
  selector: "app-synopsis",
  templateUrl: "./synopsis.component.html",
  styleUrl: "./synopsis.component.scss",
})
export class SynopsisComponent implements OnInit {
  isLoading = false;
  currentUrl = "";
  userId: string | null = null;
  downloadingConsent = false;
  profileInfo: ProfileInfo | null = null;
  metrics: DonorMetric[] | null = [];
  comprehensiveQuestionnaire: QuestionnaireTemplate | null = null;
  donorBasicMetricsMap: Record<string, string> = {};
  donorPhysicalAndMentalMetricsMap: Record<string, string> = {};
  donorHealthHistoryMetricsMap: Record<string, string> = {};
  donorFamilyHistoryMetricsMap: Record<string, string> = {};
  donorSocialHistoryMetricsMap: Record<string, string> = {};
  donorDietHistoryMetricsMap: Record<string, string> = {};
  profileMetrics: any;

  isDonorBasicHistoryNA = false;
  isDonorHealthHistoryNA = false;
  isDonorFitnessHistoryNA = false;
  isDonorFamilyHistoryNA = false;
  isDonorSocialHistoryNA = false;
  isDonorDietHistoryNA = false;

  groupedBasicDonorMetrics: Grouped = { all: [], yes: [], no: [] };

  groupedPhyscialAndMentalDonorMetrics: Grouped = { all: [], yes: [], no: [] };

  groupedHealthHistoryDonorMetrics: Grouped = { all: [], yes: [], no: [] };

  groupedFamilyHistoryDonorMetrics: Grouped = { all: [], yes: [], no: [] };

  groupedSocialHistoryDonorMetrics: Grouped = { all: [], yes: [], no: [] };

  groupedDietHistoryDonorMetrics: Grouped = { all: [], yes: [], no: [] };

  isSavingRecipient = false;
  yellowBasicInfo: string[] = [];
  redBasicInfo: string[] = [];

  // Properties for expanded rows functionality
  expandedRows = new Set<string>();
  questionnaireResponses: Record<string, JourneyQuestion[]> = {};
  loadingResponses = new Set<string>();
  translatedMetricKeys = new Set<string>();
  translatedDetailedResponses: Record<string, TranslationState> = {};

  // Performance: sets and maps for cheap lookups
  private readonly MOTIVATION_LEVEL_KEY = "DonorMotivationLevel";
  private readonly SLIDER_KEYS = new Set(["PhysicalFitnessScore", "PHQ9Score", "GAD7Score"]);
  private readonly NO_EXPAND_KEYS = new Set([
    "EnvironmentalAllergies",
    "FoodAllergies",
    "MedicalInsurance",
  ]);

  donorBasicMetricKeys = donorBasicMetricKeys;
  donorPhysicalAndMentalKeys = donorPhysicalAndMentalKeys;
  donorHealthHistoryKeys = donorHealthHistoryKeys;
  donorFamilyHistoryKeys = donorFamilyHistoryKeys;
  donorSocialHistoryKeys = donorSocialHistoryKeys;
  donorDietHistoryKeys = donorDietHistoryKeys;
  dontExpandMetricKeys = [
    "EnvironmentalAllergies",
    "FoodAllergies",
    "MedicalInsurance",
  ];


  isRecipientDobInvalid = false;

  // Scale configurations for MET, PHQ-9, and GAD-7
  metScaleConfig: ScaleConfig = {
    min: -0.1,
    max: 11.1,
    greenRange: [-0.1, 5],
    yellowRange: [5, 7],
    redRange: [7, 11.1],
    unit: "METs",
    reverse: true,
  };

  phqScaleConfig: ScaleConfig = {
    min: -0.1,
    max: 27.1,
    greenRange: [-0.1, 9],
    yellowRange: [9, 15],
    redRange: [15, 27.1],
    unit: "",
    reverse: false,
  };

  gadScaleConfig: ScaleConfig = {
    min: -0.1,
    max: 21.1,
    greenRange: [-0.1, 8],
    yellowRange: [8, 11],
    redRange: [11, 21.1],
    unit: "",
    reverse: false,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contentService: ContentService,
    private questionnaireService: QuestionnaireService,
    private responseService: ResponseService,
    private modalService: NgbModal,
    private authService: AuthService,
    private navigationService: NavigationService
  ) {}

  ngOnInit(): void {
    this.userId = this.route.parent?.snapshot.paramMap.get("userId") || null;
    if (this.userId) {
      this.loadAllData();
    } else {
      // TODO: Show an error message
    }
  }

  private loadAllData(): void {
    this.isLoading = true;
    forkJoin([
      this.contentService
        .getDonorAndRecipientProfileInfo(this.userId as string)
        .pipe(
          catchError((error) => {
            console.error("Error fetching profile info:", error);
            return of(null);
          })
        ),
      this.questionnaireService.getDonorMetrics(this.userId as string).pipe(
        catchError((error) => {
          console.error("Error fetching donor metrics:", error);
          return of(null);
        })
      ),
      this.questionnaireService
        .getQuestionnaireByName("comprehensiveQuestionnaire")
        .pipe(
          catchError((error) => {
            console.error("Error fetching comprehensive questionnaire:", error);
            return of(null);
          })
        ),
    ]).subscribe({
      next: ([profileInfo, metrics, comprehensiveQuestionnaire]) => {
        this.profileInfo = profileInfo;
        this.metrics = metrics;
        this.comprehensiveQuestionnaire =
          comprehensiveQuestionnaire?.template || null;
        if (this.metrics) {
          this.donorBasicMetricsMap = this.extractMetrics(
            this.metrics,
            this.donorBasicMetricKeys
          );

          this.checkIfAllValuesAreNA(this.donorBasicMetricsMap, "basicMetrics");

          this.donorPhysicalAndMentalMetricsMap = this.extractMetrics(
            this.metrics,
            this.donorPhysicalAndMentalKeys
          );

          this.checkIfAllValuesAreNA(
            this.donorPhysicalAndMentalMetricsMap,
            "fitnessMetrics"
          );

          this.donorHealthHistoryMetricsMap = this.extractMetrics(
            this.metrics,
            this.donorHealthHistoryKeys
          );

          this.checkIfAllValuesAreNA(
            this.donorHealthHistoryMetricsMap,
            "healthHistory"
          );

          this.donorFamilyHistoryMetricsMap = this.extractMetrics(
            this.metrics,
            this.donorFamilyHistoryKeys
          );

          this.checkIfAllValuesAreNA(
            this.donorFamilyHistoryMetricsMap,
            "familyHistory"
          );

          this.donorSocialHistoryMetricsMap = this.extractMetrics(
            this.metrics,
            this.donorSocialHistoryKeys
          );

          this.checkIfAllValuesAreNA(
            this.donorSocialHistoryMetricsMap,
            "socialHistory"
          );

          this.donorDietHistoryMetricsMap = this.extractMetrics(
            this.metrics,
            this.donorDietHistoryKeys
          );

          this.checkIfAllValuesAreNA(
            this.donorDietHistoryMetricsMap,
            "dietHistory"
          );

          const basicDonorMetrics = this.getGroupedDonorMetrics(
            basicMetricsLabels,
            this.donorBasicMetricKeys,
            this.donorBasicMetricsMap
          );
          this.groupedBasicDonorMetrics = basicDonorMetrics;
          this.translateMotivationLevelIfNeeded();

          const physicalAndMentalHistoryDonorMetrics = this.getGroupedDonorMetrics(
            physicalAndMentalHistoryLabels,
            this.donorPhysicalAndMentalKeys,
            this.donorPhysicalAndMentalMetricsMap
          );
          // Sort YES rows so yellow-highlight rows appear first
          const yesSorted = [...physicalAndMentalHistoryDonorMetrics.yes].sort((a, b) => {
            const ay = this.isYesYellow(a) ? 0 : 1;
            const by = this.isYesYellow(b) ? 0 : 1;
            return ay - by; // yellow (0) before non-yellow (1)
          });
          this.groupedPhyscialAndMentalDonorMetrics = {
            ...physicalAndMentalHistoryDonorMetrics,
            yes: yesSorted,
          };

          const healthHistoryDonorMetrics = this.getGroupedDonorMetrics(
            healthHistoryLabels,
            this.donorHealthHistoryKeys,
            this.donorHealthHistoryMetricsMap
          );
          this.groupedHealthHistoryDonorMetrics = healthHistoryDonorMetrics;

          const familyHistoryDonorMetrics = this.getGroupedDonorMetrics(
            familyHistoryLabels,
            this.donorFamilyHistoryKeys,
            this.donorFamilyHistoryMetricsMap
          );
          this.groupedFamilyHistoryDonorMetrics = familyHistoryDonorMetrics;

          const socialHistoryDonorMetrics = this.getGroupedDonorMetrics(
            socialHistoryLabels,
            this.donorSocialHistoryKeys,
            this.donorSocialHistoryMetricsMap
          );

          this.groupedSocialHistoryDonorMetrics = socialHistoryDonorMetrics;

          const dietHistoryDonorMetrics = this.getGroupedDonorMetrics(
            dietHistoryLabels,
            this.donorDietHistoryKeys,
            this.donorDietHistoryMetricsMap
          );

          this.groupedDietHistoryDonorMetrics = dietHistoryDonorMetrics;
        }

        

        const donor = this.profileInfo?.data?.donor as any;
        const logic = environment.ineligibility_logic;

        if (donor) {
          const { dateOfBirth, bmi } = donor;

          const age = parseInt(calculateAge(dateOfBirth));

          this.contentService.evaluateMetric(
            age,
            logic.age,
            "age",
            this.redBasicInfo,
            this.yellowBasicInfo
          );
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error("Error fetching data:", err);
        this.isLoading = false;
      },
    });
  }

  getGroupedDonorMetrics(
    labels: Record<string, string>,
    metricKeys: string[],
    metricMaps: Record<string, string>
  ): Grouped {
    const yesMetrics: MetricEntry[] = [];
    const noMetrics: MetricEntry[] = [];

    for (const key of metricKeys) {
      const rule = synopsisMetricRules[key] || {};

      if (rule.valueKind === "profileOnly") {
        continue;
      }

      const value = metricMaps[key];
      const entry: MetricEntry = { key, label: labels[key], value };
      const status = this.classifyMetricStatus(key, value);

      if (status === "risk" || status === "display") {
        yesMetrics.push(entry);
      } else if (
        status === "cleared" ||
        status === "notFilled" ||
        status === "notRequired"
      ) {
        noMetrics.push(entry);
      }
    }

    return {
      all: [...yesMetrics, ...noMetrics],
      yes: yesMetrics,
      no: noMetrics,
    };
  }

  private classifyMetricStatus(
    key: string,
    value: string | undefined | null
  ): MetricStatus {
    const rule = synopsisMetricRules[key] || {};

    if (this.isMetricNotRequiredByRule(key)) {
      return "notRequired";
    }

    if (this.isNotRequiredValue(value)) {
      return "notRequired";
    }

    if (this.isNAValue(value)) {
      return "notFilled";
    }

    if (rule.valueKind === "score" || rule.valueKind === "alwaysDisplay") {
      return "display";
    }

    if (rule.riskDirection === "reverse") {
      if (this.isMetricNoValue(value)) {
        return "risk";
      }

      if (this.isMetricYesValue(value)) {
        return this.hasRequiredClearedContext(key) ? "cleared" : "notFilled";
      }

      return this.hasRequiredClearedContext(key) ? "cleared" : "notFilled";
    }

    if (this.metricHasRiskSignal(value)) {
      return "risk";
    }

    return this.hasRequiredClearedContext(key) ? "cleared" : "notFilled";
  }

  getMetricStatus(metric: MetricEntry): MetricStatus {
    return this.classifyMetricStatus(metric.key, metric.value);
  }

  getMetricCellBackground(metric: MetricEntry): string {
    const status = this.getMetricStatus(metric);

    if (status === "risk") {
      return "#fffacd";
    }

    if (status === "cleared") {
      return "#ecfef3";
    }

    if (status === "notFilled") {
      return "#f2f2f2";
    }

    if (status === "notRequired") {
      return "#f2f2f2";
    }

    return "";
  }

  getMetricCellColor(metric: MetricEntry): string {
    return this.getMetricStatus(metric) === "cleared" ? "#155724" : "";
  }

  private hasRequiredClearedContext(key: string): boolean {
    const rule = synopsisMetricRules[key] || {};
    return (
      !rule.requiresResponseContextForCleared ||
      this.metricHasResponseContext(key)
    );
  }

  private metricHasResponseContext(key: string): boolean {
    const metric = this.metrics?.find((m) => m.metric_key === key);
    return !!metric?.questionnaire_id && !!metric?.subsection_id;
  }

  private isMetricNotRequiredByRule(key: string): boolean {
    const templateVisibility = this.isMetricNotRequiredByQuestionnaire(key);
    if (templateVisibility !== null) {
      return templateVisibility;
    }

    const requiredGender = synopsisMetricRules[key]?.requiredGender;
    if (!requiredGender) {
      return false;
    }

    const donorGender = this.normalizeMetricText(
      this.profileInfo?.data?.donor?.gender
    );
    if (!donorGender) {
      return false;
    }

    return donorGender !== requiredGender;
  }

  private isMetricNotRequiredByQuestionnaire(key: string): boolean | null {
    if (!["MensHealthHistory", "WomensHealthHistory"].includes(key)) {
      return null;
    }

    const matchingSubsections = this.getQuestionnaireSubsectionsForMetric(key);
    if (matchingSubsections.length === 0) {
      return null;
    }

    return matchingSubsections.every(
      (subsection) => !this.isSubsectionRequiredForDonor(subsection)
    );
  }

  private getQuestionnaireSubsectionsForMetric(key: string): Subsection[] {
    if (!this.comprehensiveQuestionnaire) {
      return [];
    }

    const matchers =
      key === "MensHealthHistory"
        ? ["mens health", "men's health", "salud masculina", "hombres"]
        : ["womens health", "women's health", "salud femenina", "mujeres"];

    return this.comprehensiveQuestionnaire.sections.flatMap((section) =>
      section.subsections.filter((subsection) =>
        this.subsectionMatches(subsection, matchers)
      )
    );
  }

  private subsectionMatches(subsection: Subsection, matchers: string[]): boolean {
    const searchableText = [
      subsection.name,
      subsection.description,
      ...subsection.questions.map((question) => question.questiion_text),
    ]
      .map((value) => this.normalizeMetricText(value))
      .join(" ");

    return matchers.some((matcher) =>
      searchableText.includes(this.normalizeMetricText(matcher))
    );
  }

  private isSubsectionRequiredForDonor(subsection: Subsection): boolean {
    const subsectionVisible = this.evaluateVisibilityLogic(
      this.parseVisibilityLogic(subsection.visibilityLogic)
    );

    if (!subsectionVisible) {
      return false;
    }

    const activeQuestions = subsection.questions.filter(
      (question) => question.is_active !== false
    );

    if (activeQuestions.length === 0) {
      return false;
    }

    return activeQuestions.some((question) =>
      this.isQuestionRequiredForDonor(question)
    );
  }

  private isQuestionRequiredForDonor(question: Question): boolean {
    return this.evaluateVisibilityLogic(question.visibility_logic);
  }

  private parseVisibilityLogic(
    logic: VisibilityLogic | string | null | undefined
  ): VisibilityLogic | null {
    if (!logic) {
      return null;
    }

    if (typeof logic !== "string") {
      return logic;
    }

    try {
      return JSON.parse(logic) as VisibilityLogic;
    } catch {
      return null;
    }
  }

  private evaluateVisibilityLogic(
    logic: VisibilityLogic | string | null | undefined
  ): boolean {
    const visibilityLogic = this.parseVisibilityLogic(logic);
    if (!visibilityLogic) {
      return true;
    }

    const dependsOnAnswer = this.getVisibilityDependsOnAnswer(visibilityLogic);
    if (dependsOnAnswer === undefined || dependsOnAnswer === null) {
      return false;
    }

    const values = this.getVisibilityComparisonValues(visibilityLogic.value);

    switch (visibilityLogic.operator) {
      case "equals":
        return Array.isArray(dependsOnAnswer)
          ? dependsOnAnswer.some((value) =>
              values.includes(this.normalizeMetricText(value))
            )
          : values.includes(this.normalizeMetricText(dependsOnAnswer));

      case "not_equals":
        return Array.isArray(dependsOnAnswer)
          ? !dependsOnAnswer.some((value) =>
              values.includes(this.normalizeMetricText(value))
            )
          : !values.includes(this.normalizeMetricText(dependsOnAnswer));

      case "greater_than":
        return Number(dependsOnAnswer) > Number(visibilityLogic.value);

      case "less_than":
        return Number(dependsOnAnswer) < Number(visibilityLogic.value);

      case "greater_than_or_equal":
        return Number(dependsOnAnswer) >= Number(visibilityLogic.value);

      case "less_than_or_equal":
        return Number(dependsOnAnswer) <= Number(visibilityLogic.value);

      default:
        return true;
    }
  }

  private getVisibilityDependsOnAnswer(
    visibilityLogic: VisibilityLogic
  ): string | number | string[] | null {
    if (visibilityLogic.dependsOn === "age") {
      const dateOfBirth = this.profileInfo?.data?.donor?.dateOfBirth;
      return dateOfBirth ? Number(calculateAge(dateOfBirth)) : null;
    }

    if (visibilityLogic.dependsOn === "profileField") {
      const fieldName = visibilityLogic.fieldName;
      return fieldName
        ? this.profileInfo?.data?.donor?.[
            fieldName as keyof ProfileInfo["data"]["donor"]
          ] || null
        : null;
    }

    return null;
  }

  private getVisibilityComparisonValues(value: string | number): string[] {
    if (typeof value === "number") {
      return [String(value)];
    }

    return String(value)
      .split("|")
      .map((item) => this.normalizeMetricText(item));
  }

  getBasicMetricValueClass(metric: MetricEntry): string {
    if (metric.key !== this.MOTIVATION_LEVEL_KEY) {
      return "background-light-yellow";
    }

    if (!metric.value || metric.value === "N/A") {
      return "background-light-yellow";
    }

    const primaryValue = metric.value.split("-")[0] || metric.value;
    const normalizedValue = primaryValue
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

    const normalizedTarget = "i m certainly open to it";

    return normalizedValue === normalizedTarget
      ? "background-light-yellow"
      : "background-light-green";
  }



  viewMRBReport() {
    this.router.navigate([`/hospital/donor/${this.userId}/mrb`]);
  }

  getConsentStatusClass(status: string | null): string {
    if (!status) return "status-not-sent";
    return statusMapping[status] || "status-not-sent";
  }

  extractMetrics(
    metrics: DonorMetric[],
    keys: string[]
  ): Record<string, string> {
    const result: Record<string, string> = {};
    for (const key of keys) {
      const item = metrics.find((m) => m.metric_key === key);
      result[key] = item?.metric_value ?? "N/A";
    }
    return result;
  }

  private translateMotivationLevelIfNeeded(): void {
    const sourceLocale = this.getDonorLocale();
    const motivationLevel = this.donorBasicMetricsMap[this.MOTIVATION_LEVEL_KEY];

    if (!motivationLevel || this.isNAValue(motivationLevel)) {
      return;
    }

    if (
      !sourceLocale.startsWith("es") &&
      !this.hasSpanishMotivationText(motivationLevel)
    ) {
      return;
    }

    this.contentService
      .translateText({
        sourceLocale: "es",
        targetLocale: "en",
        text: this.getMotivationExplanation(motivationLevel),
        context:
          "Living kidney donor initial questionnaire motivation level answer",
      })
      .subscribe({
        next: (translation) => {
          const translatedText = translation.translatedText?.trim();

          if (translatedText) {
            this.translatedMetricKeys.add(this.MOTIVATION_LEVEL_KEY);
            this.updateBasicMetricValue(
              this.MOTIVATION_LEVEL_KEY,
              this.replaceMotivationExplanation(motivationLevel, translatedText)
            );
          }
        },
        error: (error) => {
          console.error("Error translating motivation level:", error);
        },
      });
  }

  private updateBasicMetricValue(metricKey: string, value: string): void {
    this.donorBasicMetricsMap = {
      ...this.donorBasicMetricsMap,
      [metricKey]: value,
    };

    this.groupedBasicDonorMetrics = {
      all: this.updateMetricEntries(
        this.groupedBasicDonorMetrics.all,
        metricKey,
        value
      ),
      yes: this.updateMetricEntries(
        this.groupedBasicDonorMetrics.yes,
        metricKey,
        value
      ),
      no: this.updateMetricEntries(
        this.groupedBasicDonorMetrics.no,
        metricKey,
        value
      ),
    };
  }

  isMetricTranslated(metricKey: string): boolean {
    return this.translatedMetricKeys.has(metricKey);
  }

  private updateMetricEntries(
    metrics: MetricEntry[],
    metricKey: string,
    value: string
  ): MetricEntry[] {
    return metrics.map((metric) =>
      metric.key === metricKey ? { ...metric, value } : metric
      );
  }

  private getMotivationExplanation(value: string): string {
    const parts = String(value || "").split(/\s+-\s+/);
    return (parts.length > 1 ? parts.slice(1).join(" - ") : value).trim();
  }

  private replaceMotivationExplanation(
    originalValue: string,
    translatedExplanation: string
  ): string {
    const parts = String(originalValue || "").split(/\s+-\s+/);

    if (parts.length <= 1) {
      return translatedExplanation;
    }

    return `${parts[0].trim()} - ${translatedExplanation}`;
  }

  private hasSpanishMotivationText(value: string): boolean {
    const explanation = this.normalizeMetricText(
      this.getMotivationExplanation(value)
    );

    return [
      "muy",
      "motivado",
      "motivada",
      "donar",
      "hermano",
      "hermana",
      "padre",
      "madre",
      "hijo",
      "hija",
      "para",
      "porque",
    ].some((signal) => explanation.includes(signal));
  }

  private getDonorLocale(): string {
    return (
      this.profileInfo?.data?.donor?.locale ||
      this.profileInfo?.data?.donor?.preferredLocale ||
      ""
    )
      .toLowerCase()
      .trim();
  }

  checkIfAllValuesAreNA(map: Record<string, string>, type: string) {
    const allNA = Object.values(map).every((v) => this.isNAValue(v));

    switch (type) {
      case "basicMetrics":
        this.isDonorBasicHistoryNA = allNA;
        break;
      case "fitnessMetrics":
        this.isDonorFitnessHistoryNA = allNA;
        break;
      case "healthHistory":
        this.isDonorHealthHistoryNA = allNA;
        break;
      case "familyHistory":
        this.isDonorFamilyHistoryNA = allNA;
        break;
      case "socialHistory":
        this.isDonorSocialHistoryNA = allNA;
        break;
      case "dietHistory":
        this.isDonorDietHistoryNA = allNA;
        break;
      default:
        console.warn(`Unknown metrics type: ${type}`);
        break;
    }
  }

  // Methods for expanded rows functionality
  isRowExpanded(metricKey: string): boolean {
    return this.expandedRows.has(metricKey);
  }

  isMetricYes(metricKey: string): boolean {
    const metric = this.metrics?.find((m) => m.metric_key === metricKey);
    if (!metric) return false;

    return this.isMetricYesValue(metric.metric_value);
  }

  isMetricExpandable(metricKey: string): boolean {
    // Do not expand explicitly excluded keys or slider metrics
    if (this.NO_EXPAND_KEYS.has(metricKey)) return false;
    if (this.SLIDER_KEYS.has(metricKey)) return false;
    return true;
  }

  toggleRowExpansion(metricKey: string): void {
    if (this.expandedRows.has(metricKey)) {
      this.expandedRows.delete(metricKey);
    } else {
      this.expandedRows.add(metricKey);
      this.loadDetailedResponses(metricKey);
    }
  }

  loadDetailedResponses(metricKey: string): void {
    if (this.questionnaireResponses[metricKey]) {
      console.log(`Responses already loaded for ${metricKey}`);
      return;
    }

    // Find the metric to get its questionnaire_id and subsection_id
    const metric = this.metrics?.find((m) => m.metric_key === metricKey);

    if (!metric) {
      console.log(`Metric not found for key: ${metricKey}`);
      return;
    }

    if (!metric.subsection_id || !metric.questionnaire_id) {
      console.log(
        `No questionnaire_id or subsection_id found for metric: ${metricKey}`,
        metric
      );
      return;
    }

    this.loadingResponses.add(metricKey);
    this.responseService
      .getQuestionnaireResponsesByQuestionnaireId(
        metric.questionnaire_id,
        metric.subsection_id
      )
      .subscribe({
        next: (responses) => {
          this.questionnaireResponses[metricKey] = responses.filter(
            (r) => r.answerText !== null && r.answerText.trim() !== ""
          );
          this.loadingResponses.delete(metricKey);
        },
        error: (error) => {
          console.error(`Error loading responses for ${metricKey}:`, error);
          this.loadingResponses.delete(metricKey);
        },
      });
  }

  getResponsesForMetric(metricKey: string): JourneyQuestion[] {
    return this.questionnaireResponses[metricKey] || [];
  }

  isLoadingResponses(metricKey: string): boolean {
    return this.loadingResponses.has(metricKey);
  }

  getAnswerDisplayText(response: JourneyQuestion): string {
    return String(response.answerText || "").trim();
  }

  canTranslateDetailedResponse(response: JourneyQuestion): boolean {
    return (
      !this.isDonorLocaleEnglish() &&
      this.hasDetailedResponseAnswer(response) &&
      this.isOpenTextResponse(response)
    );
  }

  translateDetailedResponse(response: JourneyQuestion): void {
    if (!this.canTranslateDetailedResponse(response)) {
      return;
    }

    const key = this.getDetailedTranslationKey(response);
    const existingTranslation = this.translatedDetailedResponses[key];

    if (existingTranslation?.translatedText || existingTranslation?.loading) {
      return;
    }

    this.translatedDetailedResponses[key] = { loading: true };

    this.contentService
      .translateText({
        sourceLocale: this.getDonorLocale() || undefined,
        targetLocale: "en",
        text: this.getAnswerDisplayText(response),
        context: "Living kidney donor questionnaire open-text answer",
      })
      .pipe(
        catchError((error) => {
          console.error("Error translating detailed response:", error);
          this.translatedDetailedResponses[key] = {
            error: "Translation failed. Please try again.",
          };
          return of(null);
        })
      )
      .subscribe((translation) => {
        const translatedText = translation?.translatedText?.trim();

        if (translatedText) {
          this.translatedDetailedResponses[key] = { translatedText };
        }
      });
  }

  getDetailedTranslationState(
    response: JourneyQuestion
  ): TranslationState | null {
    return (
      this.translatedDetailedResponses[this.getDetailedTranslationKey(response)] ||
      null
    );
  }

  getDetailedTranslatedDisplayText(response: JourneyQuestion): string {
    return String(
      this.getDetailedTranslationState(response)?.translatedText || ""
    ).trim();
  }

  private hasDetailedResponseAnswer(response: JourneyQuestion): boolean {
    return this.getAnswerDisplayText(response).length > 0;
  }

  private isOpenTextResponse(response: JourneyQuestion): boolean {
    const questionType = (response.questionType || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return ["short", "long", "text", "textarea", "short_text", "long_text"].includes(
      questionType
    );
  }

  private isDonorLocaleEnglish(): boolean {
    const locale = this.getDonorLocale();
    return !locale || locale.startsWith("en");
  }

  private getDetailedTranslationKey(response: JourneyQuestion): string {
    return [
      response.questionId || response.questionText,
      response.answerText,
      "en",
    ].join("|");
  }

  isAnswerYes(answerText: string | null | undefined): boolean {
    return this.isMetricYesValue(answerText);
  }

  /**
   * Check if the metric key is MedicalInsurance
   */
  isMedicalInsurance(metricKey: string): boolean {
    return metricKey === "MedicalInsurance";
  }

  get nonMedicalInsuranceSocialHistoryNo(): { key: string; label: string; value: string }[] {
    return this.groupedSocialHistoryDonorMetrics.no.filter(
      (m) => !this.isMedicalInsurance(m.key) || this.isMetricNotFilled(m)
    );
  }

  get medicalInsuranceSocialHistoryNo(): { key: string; label: string; value: string }[] {
    return this.groupedSocialHistoryDonorMetrics.no.filter(
      (m) => this.isMedicalInsurance(m.key) && !this.isMetricNotFilled(m)
    );
  }

  getClearedNoMetrics(metrics: MetricEntry[]): MetricEntry[] {
    return metrics.filter((metric) => this.getMetricStatus(metric) === "cleared");
  }

  getNotFilledNoMetrics(metrics: MetricEntry[]): MetricEntry[] {
    return metrics.filter((metric) => this.isMetricNotFilled(metric));
  }

  getNotRequiredNoMetrics(metrics: MetricEntry[]): MetricEntry[] {
    return metrics.filter(
      (metric) => this.getMetricStatus(metric) === "notRequired"
    );
  }

  isMetricNotFilled(metric: MetricEntry): boolean {
    return this.getMetricStatus(metric) === "notFilled";
  }

  /** Fitness helpers for Risk Screening (PHSPositive) */
  isRiskScreening(metricKey: string): boolean {
    return metricKey === "PHSPositive";
  }


  // Combined partition for Physical & Mental NO metrics: isolates Risk Screening if it has value
  get riskFitnessPartition(): { riskRow: MetricEntry | null; rest: MetricEntry[] } {
    const allNo = this.groupedPhyscialAndMentalDonorMetrics.no;
    const risk = allNo.find((m) => this.isRiskScreening(m.key));
    const hasReal = !!risk && !this.isNAValue(risk.value);
    const rest = allNo.filter((m) => !this.isRiskScreening(m.key) || !hasReal);
    return { riskRow: hasReal ? (risk as MetricEntry) : null, rest };
  }

  // Whether every metric in the Physical & Mental NO bucket is unfilled (N/A)
  get isFitnessNoGroupAllNA(): boolean {
    return this.riskFitnessPartition.rest.length > 0 &&
      this.riskFitnessPartition.rest.every((m) => this.isNAValue(m.value));
  }

  getRiskScreeningDisplayText(value: string | undefined | null): string {
    const v = this.normalizeMetricText(value);
    if (this.isMetricYesValue(value)) return "Risk Identified";
    // Treat explicit "no" and "n/a" as cleared per requirement
    if (this.isMetricNoValue(value) || v === "n/a") return "Cleared";
    return "N/A";
  }

  isMetricYesValue(value: string | undefined | null): boolean {
    const v = this.normalizeMetricText(value);
    return /\byes\b/.test(v) || /\bsi\b/.test(v);
  }

  isMetricNoValue(value: string | undefined | null): boolean {
    const v = this.normalizeMetricText(value);
    return /\bno\b/.test(v);
  }

  private metricHasRiskSignal(value: string | undefined | null): boolean {
    if (this.metricHasClearingAnswerSignal(value)) {
      return false;
    }

    return (
      this.metricHasPositiveAnswerSignal(value) ||
      this.metricHasFamilyHistorySignal(value)
    );
  }

  private metricHasPositiveAnswerSignal(value: string | undefined | null): boolean {
    const v = this.normalizeMetricText(value);
    const hasAffirmativeAnswer = /\b(yes|si)\b/.test(v);

    if (hasAffirmativeAnswer) {
      return true;
    }

    return [
      "current",
      "actual",
      "regularly",
      "regularmente",
      "socially",
      "socialmente",
      "former",
      "anterior",
      "anteriormente",
    ].some((signal) => v.includes(signal));
  }

  private metricHasClearingAnswerSignal(value: string | undefined | null): boolean {
    const v = this.normalizeMetricText(value);

    if (/\b(no|none)\b/.test(v)) {
      return true;
    }

    return [
      "i have no history",
      "i have no family history",
      "no history",
      "no family history",
      "sin antecedentes",
      "sin antecedentes familiares",
      "no tengo antecedentes",
      "no tengo antecedentes familiares",
    ].some((signal) => v.includes(signal));
  }

  private metricHasFamilyHistorySignal(value: string | undefined | null): boolean {
    const v = this.normalizeMetricText(value);
    return [
      "father",
      "mother",
      "parent",
      "grandparent",
      "sibling",
      "children",
      "child",
      "padre",
      "madre",
      "padres",
      "abuelo",
      "abuela",
      "abuelos",
      "hermano",
      "hermana",
      "hermanos",
      "hijo",
      "hija",
      "hijos",
    ].some((signal) => v.includes(signal));
  }

  private normalizeMetricText(
    value: string | number | undefined | null
  ): string {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  }

  private isNAValue(value: string | undefined | null): boolean {
    if (value == null) return true;
    const v = this.normalizeMetricText(value);
    if (v === "") return true;
    return [
      "n/a",
      "na",
      "not filled out",
      "not answered",
      "unanswered",
      "not started",
      "no completado",
      "sin responder",
    ].includes(v);
  }

  private isNotRequiredValue(value: string | undefined | null): boolean {
    const v = this.normalizeMetricText(value);
    return [
      "not required",
      "not applicable",
      "not needed",
      "no requerido",
      "no requerida",
      "no aplica",
      "no aplicable",
    ].includes(v);
  }

  // Determine if a YES-row in Physical & Mental should be yellow-highlighted
  private isYesYellow(entry: MetricEntry): boolean {
    // Slider metrics (Fitness, PHQ9, GAD7) should not be highlighted
    if (this.SLIDER_KEYS.has(entry.key)) return false;
    // Risk screening is yellow only when 'Risk Identified'
    if (this.isRiskScreening(entry.key)) {
      return this.getRiskScreeningDisplayText(entry.value) === 'Risk Identified';
    }
    // Other non-slider YES metrics are yellow
    return true;
  }



  openEpicLookupModal(type: "Donor" | "Recipient") {
    const donorUserId = this.profileInfo?.data?.donor?.userId;

    const modalRef = this.modalService.open(EpicLookupModalComponent, {
      centered: true,
      backdrop: "static",
      size: "xl",
    });

    // Pass donorUserId as component input
    modalRef.componentInstance.donorUserId = donorUserId;
    modalRef.componentInstance.type = type;

    // Refresh users when modal closes
    modalRef.result
      .then((result) => {
        console.log(result);
        if (result.data.updatedFields.epicPatientId) {
          this.isLoading = true;
          this.loadAllData();
        }
      })
      .catch(() => {});
  }

  get filteredGroupedHealthHistoryDonorMetrics() {
    return this.groupedHealthHistoryDonorMetrics;
  }

  getMetricValue(metricKey: string): number | null {
    // Check which metrics map contains this key
    let metricValue: string | undefined;

    if (this.donorPhysicalAndMentalKeys.includes(metricKey)) {
      metricValue = this.donorPhysicalAndMentalMetricsMap[metricKey];
    } else if (this.donorBasicMetricKeys.includes(metricKey)) {
      metricValue = this.donorBasicMetricsMap[metricKey];
    } else if (this.donorHealthHistoryKeys.includes(metricKey)) {
      metricValue = this.donorHealthHistoryMetricsMap[metricKey];
    } else if (this.donorFamilyHistoryKeys.includes(metricKey)) {
      metricValue = this.donorFamilyHistoryMetricsMap[metricKey];
    } else if (this.donorSocialHistoryKeys.includes(metricKey)) {
      metricValue = this.donorSocialHistoryMetricsMap[metricKey];
    } else if (this.donorDietHistoryKeys.includes(metricKey)) {
      metricValue = this.donorDietHistoryMetricsMap[metricKey];
    }

    if (!metricValue || metricValue === "N/A" || metricValue === "") {
      return null;
    }

    // Special handling for PhysicalFitnessScore with ">10" value
    if (metricKey === "PhysicalFitnessScore" && metricValue.includes(">10")) {
      return 11;
    }

    const value = parseFloat(metricValue);
    return isNaN(value) ? null : value;
  }

  shouldShowSlidingScale(metricKey: string): boolean {
    return this.SLIDER_KEYS.has(metricKey);
  }

  getScaleConfig(metricKey: string): ScaleConfig | null {
    const map: Record<string, ScaleConfig> = {
      PhysicalFitnessScore: this.metScaleConfig,
      PHQ9Score: this.phqScaleConfig,
      GAD7Score: this.gadScaleConfig,
    };
    return map[metricKey] ?? null;
  }

  // trackBy helpers
  trackByMetric = (_: number, m: { key: string }) => m.key;
  trackByResponse = (_: number, r: JourneyQuestion) => r.questionText;

  epicUserSelected(){
    this.loadAllData();
  }

  navigateToDashboard(): void {
    this.navigationService.navigateToDashboard();
  }
}
